import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Trophy, Loader2, TrendingUp, Medal } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CORES_MEDALHA = {
  verde: "bg-green-600",
  bronze: "bg-amber-700",
  prata: "bg-gray-400",
  ouro: "bg-yellow-500",
  diamante: "bg-cyan-400",
};

/**
 * Ranking de afiliados por vendas confirmadas no mês - peça que
 * faltava, identificada na varredura: só o fornecedor tinha ranking de
 * desempenho, o afiliado (quem gera a venda) não tinha nada parecido.
 */
const RankingAfiliados = ({ usuarioId }) => {
  const [ranking, setRanking] = useState([]);
  const [meuDesempenho, setMeuDesempenho] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const carregar = async () => {
      setCarregando(true);
      try {
        const [respRanking, respMeu] = await Promise.all([
          authService.fetchAuth(`${BACKEND_URL}/api/ranking-afiliados/geral`),
          usuarioId
            ? authService.fetchAuth(`${BACKEND_URL}/api/ranking-afiliados/meu/${usuarioId}`)
            : Promise.resolve(null),
        ]);
        if (respRanking.ok) setRanking(await respRanking.json());
        if (respMeu && respMeu.ok) setMeuDesempenho(await respMeu.json());
      } catch (error) {
        console.error("Erro ao carregar ranking:", error);
      } finally {
        setCarregando(false);
      }
    };
    carregar();
  }, [usuarioId]);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Trophy className="w-7 h-7 text-yellow-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Ranking de Afiliados</h1>
          <p className="text-sm text-gray-400">Vendas confirmadas neste mês</p>
        </div>
      </div>

      {meuDesempenho && (
        <Card className="p-4 bg-orange-950/30 border-orange-800 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-orange-300">Sua posição</p>
              <p className="text-white font-bold text-lg">
                {meuDesempenho.posicao ? `${meuDesempenho.posicao}º lugar` : "Ainda sem vendas este mês"}
              </p>
            </div>
            <div className="text-right">
              <span className={`text-[10px] font-semibold px-2 py-1 rounded-full text-white ${CORES_MEDALHA[meuDesempenho.medalha.cor]}`}>
                {meuDesempenho.medalha.nome}
              </span>
              <p className="text-xs text-gray-400 mt-1">{meuDesempenho.vendas_no_mes} vendas</p>
            </div>
          </div>
        </Card>
      )}

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : ranking.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          Nenhuma venda confirmada neste mês ainda. Seja o primeiro!
        </Card>
      ) : (
        <div className="space-y-2">
          {ranking.map((r) => (
            <Card
              key={r.afiliado_id}
              className={`p-3 flex items-center gap-3 ${
                r.afiliado_id === usuarioId ? "bg-orange-950/30 border-orange-700" : "bg-gray-800/50 border-gray-700"
              }`}
            >
              <div className="w-8 text-center">
                {r.posicao <= 3 ? (
                  <Medal className={`w-5 h-5 mx-auto ${r.posicao === 1 ? "text-yellow-400" : r.posicao === 2 ? "text-gray-300" : "text-amber-600"}`} />
                ) : (
                  <span className="text-gray-500 font-medium text-sm">{r.posicao}</span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{r.nome}</p>
                <p className="text-xs text-gray-400">{r.vendas_no_mes} vendas · R$ {r.receita_no_mes.toFixed(2)}</p>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-1 rounded-full text-white ${CORES_MEDALHA[r.medalha.cor]}`}>
                {r.medalha.nome}
              </span>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RankingAfiliados;
